import cn from 'classnames';
import { useEffect, useState } from 'react';

import type {
    IDataPoint,
    IDataPointOption,
    IQuotaTable,
    IQuotaTableCondition,
} from '@webapp/account/src/resources/respondents';

import { Arrow } from '@webapp/common/assets/icons';
import { Button } from '@webapp/common/components/button';
import { Checkbox } from '@webapp/common/components/checkbox';
import { Modal } from '@webapp/common/components/modal';
import { Textfield } from '@webapp/common/components/textfield';

import css from './criteria-modal.css';

export const CriteriaModal: FC<{
    points: Array<IDataPoint>;
    editCriteriaCode?: string;
    savedCriteriaList: Array<IQuotaTable>;
    quota: number;
    onSave: (data: any, isEdit: boolean) => void;
    onClose: () => void;
}> = ({ points, editCriteriaCode, savedCriteriaList, quota, onSave, onClose }) => {
    const editCriteria = editCriteriaCode ? savedCriteriaList.find((item) => item.point === editCriteriaCode) : null;
    const [criteria, setCriteria] = useState<IDataPoint | null>(
        editCriteriaCode ? points.find((item) => item.code === editCriteriaCode) : null,
    );
    const [searchValue, setSearchValue] = useState<string>('');
    const [conditions, setConditions] = useState<Array<IQuotaTableCondition>>(editCriteria?.conditions || []);

    const isEdit = Boolean(editCriteriaCode);
    const isLongList = ['qrucity', 'qruregion'].includes(criteria?.code);
    const isSingle = ['qchildren', 'qfamilystatus', 'qfamilymembers'].includes(criteria?.code);

    useEffect(() => {
        setSearchValue('');
    }, [criteria]);

    const onClickPoint = (option: IDataPointOption, isAdd?: boolean) => {
        if (isSingle) {
            setConditions([{ code: option.code, count: quota }]);
        } else {
            setConditions((prev) => {
                return (
                    isAdd
                        ? [
                              ...prev,
                              {
                                  code: option.code,
                                  count: 0,
                              },
                          ]
                        : prev.filter((item) => item.code !== option.code)
                ).map((item, index, arr) => {
                    const newCount = Math.floor(quota / arr.length);

                    return { ...item, count: index === 0 ? newCount + (quota % newCount) : newCount };
                });
            });
        }
    };

    return (
        <Modal acceptText='Добавить' className={css.modal} visible={true} onClose={onClose}>
            <div className={css.title}>Выберите критерии</div>
            <div className={css.modalContent}>
                {!criteria ? (
                    <div className={css.modalContentList}>
                        {points.map((point, index) => {
                            if (savedCriteriaList.find((item) => item.point === point.code) || point.code === 'qage') {
                                return null;
                            }

                            return (
                                <div
                                    key={`${point.title}-${index}`}
                                    onClick={() => {
                                        setCriteria(point);
                                        setConditions([]);
                                    }}
                                    className={css.modalContentListItem}
                                >
                                    {point.title}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className={css.modalContentOptions}>
                        {!isEdit && (
                            <a className={css.arrowLinkBack} onClick={() => setCriteria(null)}>
                                <span className={css.arrowLinkBackIcon}>
                                    <Arrow />
                                </span>
                                Назад
                            </a>
                        )}
                        <div>{criteria.title}</div>
                        {isLongList && <Textfield placeholder='Поиск' value={searchValue} onChange={setSearchValue} />}
                        <div
                            className={cn(css.modalContentOptionsList, { [css.modalContentOptionsList2]: isLongList })}
                        >
                            {criteria.options.map((item) => {
                                if (searchValue && !item.title.toLowerCase().includes(searchValue.toLowerCase())) {
                                    return null;
                                }

                                return (
                                    <Checkbox
                                        checked={Boolean(conditions.find((cond) => cond.code === item.code))}
                                        key={item.code}
                                        onChange={(checked: boolean) => onClickPoint(item, checked)}
                                        label={item.title}
                                        round={isSingle}
                                    />
                                );
                            })}
                        </div>
                        <Button
                            onClick={() => {
                                onSave(
                                    {
                                        point: criteria.code,
                                        conditions,
                                    },
                                    isEdit,
                                );
                                onClose();
                            }}
                        >
                            Добавить
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
};
